import { NoticeBlock, type NoticeBlockProps } from "@/components/NoticeBlock.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { CreateBillingAccount } from "@/components/forms/CreateBillingAccount.tsx";
import { AddPaymentMethodButton, TopUpNowButton } from "@/modules/billing/ActionButtons.tsx";
import { AccountIsBeingValidatedText } from "@/modules/billing/NoValidBillingAccountNotice.tsx";
import { billingStateAtom } from "@/modules/billing/billingLogic.tsx";
import { firstAccountNoticesAtom } from "@/modules/billing/firstAccountNotices.store.tsx";
import { billingAccountLink } from "@/modules/billing/links.ts";
import { getPromotionNotices } from "@/modules/billing/promotionNotices.tsx";
import { useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans } from "@lingui/react/macro";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useAtomValue } from "jotai/react";
import type { ReactNode } from "react";
import { first } from "remeda";

/** Billing account promotions, warnings and call to action notices. */
export function BillingAccountNotices() {
    //#region Hooks
    const data = useSuspenseQueryAtom(billingStateAtom);
    const firstAccountNotices = useAtomValue(firstAccountNoticesAtom);
    //#endregion

    let items: ReactNode[] = [];
    let button: NoticeBlockProps["button"];
    let color: NoticeBlockProps["color"];

    const { callToAction } = data;
    const { action } = callToAction;

    const promotions = getPromotionNotices(data.accounts);

    switch (action) {
        case "create_account": {
            button = <CreateBillingAccount />;

            items.push(
                <p key="first">
                    <Trans>
                        To start using the platform you need to first setup billing account. You are welcome to click
                        around to familiarize yourself with the system and come back to billing account creation at any
                        time you are ready to start building your first resources.
                    </Trans>
                </p>,
            );

            if (firstAccountNotices.length) {
                items.push(
                    <ul key="options" className="m-0 pl-5">
                        {firstAccountNotices.map(({ content, id }) => (
                            <li key={id}>{content}</li>
                        ))}
                    </ul>,
                );
            }

            break;
        }
        case "wait_for_verification":
        case "must_top_up":
        case "add_payment_method": {
            const { account } = callToAction;
            items.push(
                <p key="no_active">
                    <Trans>Without an active billing account, resources can not be created.</Trans>
                </p>,
            );
            switch (action) {
                case "wait_for_verification":
                case "add_payment_method":
                    button = <AddPaymentMethodButton account={account} />;
                    items.push(
                        <p key="bill">
                            <Trans>
                                To activate a billing account, please add a credit card as a payment method or contact
                                support to enable invoice payments.
                            </Trans>
                        </p>,
                    );
                    break;
                case "must_top_up":
                    button = <TopUpNowButton account={account} />;
                    items.push(
                        <p key="bill">
                            <Trans>Your billing account must have positive balance to get activated.</Trans>
                        </p>,
                    );
                    break;
            }
            break;
        }
        case "contact_support": {
            const { account } = callToAction;
            color = "error";
            button = (
                <WButton color="error" action={billingAccountLink(account)}>
                    <Trans>View details</Trans>
                </WButton>
            );
            items = [
                <p key="suspended">
                    <Trans>
                        Your billing account has been suspended. See billing account details for more information and{" "}
                        <b>contact support</b> to resolve the issue.
                    </Trans>
                </p>,
            ];
            break;
        }
        case "pending": {
            color = "primary";
            button = null;
            items = [
                <p key="pending">
                    <AccountIsBeingValidatedText />
                </p>,
            ];
            break;
        }
        case null: {
            // If there was an immediate action to take, we used that, now try to use the promotion action.

            // Arbitrarily pick the first promotion as the one for which we display an action.
            const promotion = first(promotions);
            if (promotion) {
                const promotionAction = promotion.action;
                if (promotionAction === "top_up") {
                    button = <TopUpNowButton account={promotion.account} />;
                } else {
                    exhaustiveSwitchCheck(promotionAction);
                }
            }
            break;
        }
        default:
            exhaustiveSwitchCheck(action);
    }

    for (const { id, content } of promotions) {
        items.push(<div key={id}>{content}</div>);
    }

    if (!items.length) {
        return null;
    }

    return (
        <NoticeBlock icon="jp-warning-icon" button={button} color={color}>
            {items}
        </NoticeBlock>
    );
}
